import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, ApiError } from "../lib/api";
import type { ApplianceStatus } from "../pages/SystemPage";
import { isUpdateActive, updateStageLabels, updateView, type UpdateIntent as Intent, type UpdateRequestError } from "../lib/appliance-update-state";
export { isUpdateActive, updateStageLabels } from "../lib/appliance-update-state";

const storageKey = "vicnet:appliance-update";
const changeEvent = "vicnet:appliance-update-start";
function storedIntent(): Intent | null {
  try { const v = JSON.parse(sessionStorage.getItem(storageKey) ?? "null"); return v && typeof v.version === "string" && typeof v.startedAt === "number" ? v : null; } catch { return null; }
}
export function startApplianceUpdate(version: string) {
  if (storedIntent()) return;
  const intent: Intent = { version, startedAt: Date.now() };
  sessionStorage.setItem(storageKey, JSON.stringify(intent));
  window.dispatchEvent(new CustomEvent(changeEvent, { detail: intent }));
}
export function ApplianceUpdateGate({ children }: { children: ReactNode }) {
  const [intent, setIntent] = useState(storedIntent);
  const [requestError, setRequestError] = useState<UpdateRequestError | null>(null);
  const [history, setHistory] = useState<string[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(`${storageKey}:log`) ?? "[]"); } catch { return []; }
  });
  const sending = useRef(false);
  const client = useQueryClient();
  const status = useQuery({ queryKey: ["appliance-update-progress"], queryFn: () => api<ApplianceStatus>(`/system/appliance?fresh=${Date.now()}`, { cache: "no-store" }), enabled: Boolean(intent), refetchInterval: intent ? 2_000 : false, retry: false, refetchIntervalInBackground: true });
  const installation = status.data?.updateInstallation;
  const view = intent ? updateView({ intent, installation, serverVersion: status.data?.version, dataUpdatedAt: status.dataUpdatedAt, isError: status.isError, requestError }) : null;
  const { completed = false, stage = "REQUESTING", matching = false } = view ?? {};
  const accessError = status.error instanceof ApiError && [401, 403].includes(status.error.status);
  const finished = Boolean(view?.finished || accessError);
  useEffect(() => {
    const start = async (event: Event) => {
      const next = (event as CustomEvent<Intent>).detail;
      sessionStorage.removeItem(`${storageKey}:log`); setIntent(next); setRequestError(null); setHistory([]);
      if (sending.current) return;
      sending.current = true;
      try { await api("/system/appliance/update", { method: "POST", body: "{}" }); }
      catch (error) {
        // A disconnected POST can already have been accepted. Never repeat it automatically.
        if (error instanceof ApiError) setRequestError({ message: error.message, definitive: [400, 401, 403, 404, 409, 422].includes(error.status), settledAt: Date.now() });
      } finally { sending.current = false; void client.invalidateQueries({ queryKey: ["appliance-update-progress"] }); }
    };
    window.addEventListener(changeEvent, start);
    return () => window.removeEventListener(changeEvent, start);
  }, [client]);
  useEffect(() => {
    if (!intent && installation && isUpdateActive(installation.state) && installation.targetVersion) {
      const next = { version: installation.targetVersion, startedAt: Date.now() };
      sessionStorage.setItem(storageKey, JSON.stringify(next)); setIntent(next);
    }
  }, [intent, installation]);
  useEffect(() => {
    if (!intent) return;
    const label = updateStageLabels[stage] ?? "Actualización en curso";
    setHistory((previous) => previous.at(-1)?.endsWith(label) ? previous : [...previous, `${new Date().toLocaleTimeString()} — ${label}`]);
  }, [intent, stage]);
  useEffect(() => {
    if (!intent) return;
    const previous = document.body.style.overflow; document.body.style.overflow = "hidden";
    const preventLeave = (event: BeforeUnloadEvent) => { if (!finished) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", preventLeave);
    return () => { document.body.style.overflow = previous; window.removeEventListener("beforeunload", preventLeave); };
  }, [intent, finished]);
  useEffect(() => { if (intent) sessionStorage.setItem(`${storageKey}:log`, JSON.stringify(history)); }, [history, intent]);
  useEffect(() => {
    if (!completed) return;
    const timer = window.setTimeout(() => {
      sessionStorage.removeItem(storageKey); sessionStorage.removeItem(`${storageKey}:log`);
      setIntent(null); setRequestError(null); void client.invalidateQueries();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [completed, client]);
  function close() { sessionStorage.removeItem(`${storageKey}:log`); sessionStorage.removeItem(storageKey); setIntent(null); setRequestError(null); void client.invalidateQueries(); window.location.reload(); }
  return <><div inert={intent ? true : undefined} aria-hidden={intent ? true : undefined}>{children}</div>{intent && <div className="update-lock" role="dialog" aria-modal="true" aria-labelledby="update-lock-title"><section className="panel update-lock-card" tabIndex={-1} >
    {completed ? <CheckCircle2 size={36} /> : finished ? <AlertTriangle size={36} /> : <LoaderCircle size={36} className="spin" />}
    <h1 id="update-lock-title">{completed ? `Versión ${intent.version} instalada` : accessError ? "Ingresá para continuar el seguimiento" : finished ? "La actualización no se completó" : `Actualizando a ${intent.version}`}</h1>
    <p role="status" aria-live="polite">{accessError ? "La sesión perdió acceso al seguimiento. Volvé a la aplicación e ingresá nuevamente; el proceso del servidor continúa." : view?.message}</p>
    {!finished && <p>La interfaz permanecerá bloqueada hasta completar la actualización. Los servicios se reiniciarán y la aplicación puede dejar de responder temporalmente. El proceso continúa en el servidor; reconectamos automáticamente.</p>}
    {status.isError && !finished && <p role="status">Esperando que el servidor vuelva a responder… No vuelvas a iniciar la actualización.</p>}
    {!finished && !status.isError && requestError && !requestError.definitive && stage === "REQUESTING" && <p>La solicitud todavía no pudo confirmarse. Seguimos consultando el estado sin repetirla.</p>}
    <progress aria-label="Etapas de actualización" max={100} value={completed ? 100 : !status.isError && matching && !["DOWNLOADING", "REQUESTING", "RECONNECTING"].includes(stage) ? installation?.progress : undefined} />
    <p className="muted">El progreso indica etapas verificadas. Durante la descarga el indicador es indeterminado.</p>
    <details open><summary>Registro de progreso</summary><ol className="update-log">{history.map((line, i) => <li key={i}>{line}</li>)}</ol></details>
    <p>Seguimiento por SSH: <code>sudo vicnet-appliance update-status</code></p>
    {completed && <p role="status">Actualización completada. Volverás automáticamente a la aplicación.</p>}
    {finished && <><p>Versión que informa el servidor: {status.data?.version ?? "sin confirmar"}.</p><button className="primary" onClick={close} autoFocus>Volver a la aplicación</button></>}
  </section></div>}</>;
}
